<template>
    <div class="region-modal">
        <TransitionModal>
            <Fixed
                v-if="show"
                @close="selectRegion(regionCode)"
            >
                <div
                    class="region-modal-scrim"
                    @click.stop.prevent="selectRegion(regionCode)"
                ></div>

                <div
                    v-qa="'region-modal'"
                    class="region-modal-content"
                    role="dialog"
                    aria-modal="true"
                    :aria-labelledby="titleElementId"
                >
                    <h3
                        :id="titleElementId"
                        class="title"
                    >
                        {{ t('region_modal.title') }}
                    </h3>

                    <p class="text">{{ t('region_modal.text') }}</p>

                    <div class="cta-button">
                        <ThButton
                            size="small"
                            type="bordered"
                            @click="selectRegion(regionCode)"
                        >
                            {{
                                t('region_modal.proceed', {
                                    currentRegion:
                                        getCountryNameFromRegion(regionCode) ||
                                        regionCode,
                                })
                            }}
                        </ThButton>
                    </div>

                    <p class="text">{{ t('region_modal.or_change') }}</p>

                    <ul class="region-list">
                        <li
                            v-for="(item, key) in filteredRegions"
                            :key="key"
                            v-qa="getCountryNameFromRegion(item.code)"
                            class="region-list-item"
                        >
                            <button
                                class="select-region-button"
                                @click="selectRegion(item.code)"
                            >
                                {{
                                    getCountryNameFromRegion(item.code) ||
                                    item.code
                                }}
                                ({{ item.code }})
                            </button>
                        </li>
                    </ul>

                    <button
                        v-qa="'region-modal-close'"
                        class="close"
                        type="button"
                        @click="selectRegion(regionCode)"
                    >
                        Close
                    </button>
                </div>
            </Fixed>
        </TransitionModal>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouteQuery } from '@vueuse/router'

import type { SelectRegionFunction } from '@/plugins/region'
import { useRegionPlugin } from '@/plugins/region'
import { isNotUndefinedOrNull } from '@/helpers'

import Fixed from '@/components/layout/modal/Fixed.vue'
import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import type { InitQuery } from '@/graphql/api'

defineProps<{
    show: boolean
    suggestedRegion: InitQuery['regionSuggest'] | undefined
}>()

const emit = defineEmits<{
    'selectRegion': []
}>()

const { t } = useI18n()

const {
    regions,
    selectAndPersistRegion: selectRegionOg,
    regionCode,
    getCountryNameFromRegion,
} = useRegionPlugin()

const authToken = useRouteQuery('authToken')

const selectRegion: SelectRegionFunction = (regionCode) => {
    return (
        selectRegionOg(regionCode)
            .then(() => {
                emit('selectRegion')
            })
            // TODO move back to useInit
            // after teleport/suspense/navigation bug is fixed
            // https://github.com/vuejs/core/issues/7966
            .then(() => {
                authToken.value &&= null
            })
    )
}

const filteredRegions = computed(() =>
    Object.values(regions.value)
        .filter(isNotUndefinedOrNull)
        .filter((item) => item.code !== regionCode.value)
)

const titleElementId = 'region-modal-title-id'
</script>

<style lang="scss" scoped>
.fixed {
    display: flex;
    align-items: center;
    justify-content: center;

    .region-modal-scrim {
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: rgba($black, 0.3);
        position: fixed;
    }

    .region-modal-content {
        width: 100vw;
        height: 100vh;
        overflow-y: auto;
        flex-direction: column;
        position: relative;
        z-index: 2;
        background: $stone-2;
        padding: 100px 20px 50px;

        @include min-width(640) {
            max-width: 480px;
            padding: 80px 60px 60px;
            max-height: 80vh;
            height: initial;
        }

        .cta-button {
            width: 100%;
            display: flex;
            justify-content: center;
            padding: 20px 0;
        }

        .title {
            font-family: $parcel-font-stack;
            font-size: 24px;
            font-weight: 400;
            margin-bottom: 20px;
            text-align: center;
        }

        .text {
            text-align: center;
        }

        .region-list {
            display: flex;
            flex-wrap: wrap;
            padding: 40px 0 0;

            .region-list-item {
                width: 100%;
                margin: 0 0 15px;
                text-align: center;

                @include min-width(640) {
                    width: 50%;
                    margin: 0 0 5px;
                    text-align: left;
                }

                .select-region-button {
                    cursor: pointer;
                    font-family: $effra-font-stack;
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    letter-spacing: 0.05em;
                }
            }
        }

        .close {
            @include close-button;
        }
    }
}
</style>
